/*
  ════════════════════════════════════════════════════════════════════════════
  THE SHARED CLASS STRINGS

  This is the one file where the shape rules either hold across the dashboard
  or do not hold anywhere. Every button, field, table and status mark in the
  product is built from these, so a change here is a change everywhere.

  THE RULES, AND WHY EACH ONE

  1. One corner radius: 4px. Nothing is a pill.
     A fully rounded control reads as a chip or a tag — something you might
     dismiss — rather than as the button that commits the form it sits under.
     And a card rounder than the boxes inside it reads as two designs stacked.

  2. Shadows belong to overlays only.
     A shadow says "this floats above the page". A button in a toolbar does
     not float, and neither does a card. This is affordable because the canvas
     (--background) is a step darker than cards (--card), so white panels lift
     off it on tone alone. Hover is a colour change, which is what it is
     everywhere else.

  3. Field names are small capitals, not sentence-case labels.
     9.5px with letter-spacing is a column label, which is what a <th> and a
     field name actually are. 13px uppercase is a heading, which they are not.

  4. Status is a mark, not a coloured pill.
     A 7% wash, a 25% hairline, and the tone's own ink. Tailwind's stock
     `bg-green-100 text-green-700` has no dark counterpart, so it kept its
     pale ground on a near-black card and took its text with it.

  5. Figures are tabular.
     A gate board is read down the column, not across the row.
  ════════════════════════════════════════════════════════════════════════════
*/

/* Shared by every button: square, flat, and a focus ring you can actually see. */
const BTN_BASE =
  'inline-flex items-center justify-center gap-1.5 rounded-md ' +
  'text-[11.5px] font-semibold whitespace-nowrap transition-colors ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ' +
  'focus-visible:ring-offset-1 focus-visible:ring-offset-background ' +
  'disabled:opacity-50 disabled:pointer-events-none';

/* A status mark: tinted ground at ~7%, hairline at ~25%, the tone's own ink. */
const MARK =
  'inline-flex items-center gap-1 rounded-sm border px-1.5 py-[2px] ' +
  'text-[10px] font-semibold uppercase tracking-[0.06em] whitespace-nowrap';

export const styles = {
  /* ── Type ─────────────────────────────────────────────────────────────── */
  pageTitle: 'text-lg font-bold text-foreground tracking-tight leading-tight',
  sectionTitle: 'text-[12.5px] font-bold text-foreground tracking-tight',
  label: 'text-[9.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground',
  meta: 'text-[11px] text-muted-foreground leading-snug',
  figure: 'tabular-nums font-semibold text-foreground',

  /* ── Surfaces ─────────────────────────────────────────────────────────── */
  panel: 'bg-card rounded-lg border border-border',
  /* An inset strip inside a panel — a toolbar row, a footer, a summary bar. */
  panelRow: 'border-t border-border px-4 py-2.5',

  /* ── Fields ───────────────────────────────────────────────────────────── */
  input:
    'w-full bg-transparent text-foreground placeholder:text-muted-foreground/70 ' +
    'border border-input rounded-md px-2.5 py-[7px] text-[12.5px] outline-none ' +
    'transition-colors focus:border-primary focus:ring-1 focus:ring-ring ' +
    'disabled:bg-muted disabled:text-muted-foreground',
  select:
    'w-full bg-transparent text-foreground border border-input rounded-md ' +
    'px-2.5 py-[7px] text-[12.5px] outline-none transition-colors ' +
    'focus:border-primary focus:ring-1 focus:ring-ring',
  checkbox:
    'h-3.5 w-3.5 rounded-sm border-input text-primary ' +
    'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',

  /* ── Buttons ──────────────────────────────────────────────────────────── */
  btnSizes: {
    sm: 'h-7 px-2.5',
    md: 'h-8 px-3.5 max-md:px-3',
    lg: 'h-9 px-4',
  },
  btnPrimary: `${BTN_BASE} bg-primary text-primary-foreground hover:bg-primary-hover`,
  btnSecondary:
    `${BTN_BASE} bg-card text-foreground border border-border ` +
    'hover:bg-muted hover:border-foreground/30',
  btnGhost: `${BTN_BASE} text-muted-foreground hover:bg-muted hover:text-foreground`,
  /* Destructive reads as an outline until you are on it — a red block invites
     the click it is warning you about. */
  btnDanger:
    `${BTN_BASE} bg-card text-[hsl(var(--record-ink-danger))] border border-destructive/40 ` +
    'hover:bg-destructive hover:text-destructive-foreground hover:border-destructive',

  /* ── Status marks ─────────────────────────────────────────────────────── */
  markSuccess: `${MARK} bg-success/[0.07] text-[hsl(var(--record-ink-success))] border-success/25`,
  markWarning: `${MARK} bg-warning/[0.09] text-[hsl(var(--record-ink-warning))] border-warning/30`,
  markDanger: `${MARK} bg-destructive/[0.06] text-[hsl(var(--record-ink-danger))] border-destructive/25`,
  markInfo: `${MARK} bg-info/[0.07] text-[hsl(var(--record-ink-info))] border-info/25`,
  markNeutral: `${MARK} bg-muted text-[hsl(var(--record-ink-neutral))] border-border`,

  /* ── Tables ───────────────────────────────────────────────────────────────
     The head is sticky because these tables are read while scrolled; a column
     label that has scrolled away turns every figure into a guess. */
  table: 'w-full border-collapse text-[12px] text-card-foreground',
  thead:
    'sticky top-0 z-10 bg-muted text-left text-muted-foreground ' +
    'text-[9.5px] font-bold uppercase tracking-[0.08em]',
  th: 'px-3 py-2 text-left font-bold border-b border-border whitespace-nowrap',
  td: 'px-3 py-[9px] text-left border-b border-border align-middle',
  tdNum: 'px-3 py-[9px] text-right border-b border-border align-middle tabular-nums',
  trHover: 'transition-colors hover:bg-accent/60',
  trSelected: 'bg-accent',
} as const;
